import * as React from "react";
import { Link, RouteComponentProps } from "react-router-dom";
import {
  archiveCommissionConfig,
  commissionTemplates,
  createCommissionConfig,
  getCommissionConfigs,
} from "../../libs/commission_calculator";
import Loader from "../core/Loader";
import Button from "../core/Button";
import Breadcrumbs from "../core/Breadcrumbs";
import Modal from "../core/Modal";
import ModernCard from "../core/ModernCard";
import styled from "styled-components";
import { toast } from "../../utils";
import Select from "react-select";
import produce from "immer";
import { clone } from "lodash";
import * as Sentry from "@sentry/browser";

interface IProps extends RouteComponentProps<{}> {
  org: IOrganization;
}

interface IState {
  configs: ICommissionConfig[];
  loadingConfigs: boolean;
  newConfigModalOpen: boolean;
  lockedConfigIds: Set<string>;
  selectedTemplate: "HUNTER_FARMER";
}

class CommissionConfigsIndex extends React.Component<IProps, IState> {
  state: Readonly<IState> = {
    configs: [],
    loadingConfigs: true,
    newConfigModalOpen: false,
    selectedTemplate: "HUNTER_FARMER",
    lockedConfigIds: new Set(),
  };

  componentDidMount() {
    this.fetchConfigs();
  }

  async fetchConfigs() {
    const resp = await getCommissionConfigs(this.props.org.id);
    this.setState({ configs: resp, loadingConfigs: false });
  }

  createNewConfig = async () => {
    const { history, match } = this.props;
    const { selectedTemplate } = this.state;
    const resp = await createCommissionConfig(
      commissionTemplates[selectedTemplate]
    );
    this.setState({ newConfigModalOpen: false });
    history.push(`${match.url}/config/${resp.data.id}`);
  };

  lockConfig(configId) {
    this.setState(
      produce<IState>(draft => {
        const newSet = new Set(draft.lockedConfigIds);
        newSet.add(configId);
        draft.lockedConfigIds = newSet;
      })
    );
    return () =>
      this.setState(
        produce<IState>(draft => {
          const newSet = new Set(draft.lockedConfigIds);
          newSet.delete(configId);
          draft.lockedConfigIds = newSet;
        })
      );
  }

  archiveConfig = async configId => {
    const unlock = this.lockConfig(configId);
    try {
      await archiveCommissionConfig(configId);
    } catch (e) {
      toast.error("Error while archiving commission config.");
      unlock();
      Sentry.captureException(e);
    }
    await this.fetchConfigs();
    unlock();
    toast.success("Config has been archived.");
  };

  duplicateConfig = async configId => {
    const unlock = this.lockConfig(configId);
    const config = clone(this.state.configs.find(c => c.id === configId));
    if (!config) {
      throw new Error("Config doesn't exist? This shouldn't happen.");
    }
    config.name = `${config.name} (copy)`;
    const resp = await createCommissionConfig(config);
    await this.fetchConfigs();
    unlock();
  };

  render() {
    const { match } = this.props;
    const {
      configs,
      loadingConfigs,
      newConfigModalOpen,
      lockedConfigIds,
      selectedTemplate,
    } = this.state;
    return (
      <>
        <Breadcrumbs
          crumbs={[
            {
              label: "Commission Calculator",
              routerLink: true,
              route: "/",
            },
            { label: "Configurations" },
          ]}
        />
        <div className="level">
          <div className="level-left">
            <div className="level-item">
              <Link to={`/`} className={"btn btn-default"}>
                <i className="fa fa-arrow-left" /> Back
              </Link>
            </div>
            <div className="level-item">
              <Button
                onClick={() => this.setState({ newConfigModalOpen: true })}
              >
                <i className="fa fa-plus" /> New Configuration
              </Button>
            </div>
            <div className="level-item">
              <Link
                to={`${match.path}/employees`}
                className={"btn btn-default"}
              >
                <i className="fa fa-users" /> Assign Salespeople
              </Link>
            </div>
          </div>
        </div>
        <ModernCard ta="left" m="0px">
          {loadingConfigs ? (
            <Loader />
          ) : (
            <ConfigsTable
              configs={configs.filter(c => !c.archived)}
              basePath={match.path}
              archiveConfig={this.archiveConfig}
              duplicateConfig={this.duplicateConfig}
              lockedConfigIds={lockedConfigIds}
            />
          )}
        </ModernCard>
        <Modal
          title={"New Commission Config"}
          open={newConfigModalOpen}
          close={() => this.setState({ newConfigModalOpen: false })}
          submitText={"Create"}
          onSubmit={this.createNewConfig}
        >
          <p>Choose a template:</p>
          <Select
            value={selectedTemplate}
            options={[{ label: "Hunter/Farmer", value: "HUNTER_FARMER" }]}
            onChange={(o: ISelectOption) =>
              this.setState({ selectedTemplate: o.value })
            }
            clearable={false}
          />
        </Modal>
      </>
    );
  }
}

export default CommissionConfigsIndex;

const ConfigLink = styled(Link)`
  font-size: 14pt;
`;

const ConfigButtonsCell = styled.td`
  text-align: right;
`;

function ConfigsTable({
  configs,
  basePath,
  archiveConfig,
  duplicateConfig,
  lockedConfigIds,
}) {
  if (configs.length < 1) {
    return (
      <div className={"columns is-centered"}>
        <div className="column">No groups created yet. Create one above.</div>
      </div>
    );
  }

  return (
    <table className={"table"}>
      <thead>
        <tr>
          <th>Name</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {configs.map(c => (
          <tr key={c.id}>
            <td>
              <ConfigLink to={`${basePath}/config/${c.id}`}>
                {c.name}
              </ConfigLink>
            </td>
            <ConfigButtonsCell>
              <div className="btn-group">
                <button
                  className="btn btn-sm btn-default"
                  disabled={lockedConfigIds.has(c.id)}
                  onClick={() => duplicateConfig(c.id)}
                >
                  {lockedConfigIds.has(c.id) ? (
                    <i className={"fa fa-gear fa-spin"} />
                  ) : (
                    <>
                      <i className="fa fa-files-o" /> Copy
                    </>
                  )}
                </button>
                <button
                  className="btn btn-sm btn-warning"
                  onClick={() => archiveConfig(c.id)}
                  disabled={lockedConfigIds.has(c.id)}
                >
                  {lockedConfigIds.has(c.id) ? (
                    <i className={"fa fa-gear fa-spin"} />
                  ) : (
                    <>
                      <i className="fa fa-archive" /> Archive
                    </>
                  )}
                </button>
              </div>
            </ConfigButtonsCell>
          </tr>
        ))}
      </tbody>
    </table>
  );
}



// WEBPACK FOOTER //
// ./app/javascript/components/commission_calculator/CommissionConfigsIndex.tsx